import { useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { useAuthStore } from "../../store/authStore";
import Spinner from "../../components/Spinner";

const PURPOSES = ["이사", "결혼", "개업", "수술", "시험", "여행", "계약", "로또", "재물", "기타"];

const ELEMENT_COLOR: Record<string, string> = {
  목: "bg-green-900/30 text-green-400",
  화: "bg-red-900/30 text-red-400",
  토: "bg-yellow-900/30 text-yellow-400",
  금: "bg-slate-700/30 text-slate-300",
  수: "bg-blue-900/30 text-blue-400",
};

const PURPOSE_ICON: Record<string, string> = {
  이사: "🏠", 결혼: "💍", 개업: "🏪", 수술: "🏥",
  시험: "📝", 여행: "✈️", 계약: "📋", 로또: "🍀", 재물: "💰", 기타: "⭐",
};

type LuckyDateItem = {
  date: string;
  day_pillar: string;
  gan_element: string;
  ji_element: string;
  score: number;
  reason: string;
};

type LuckyDateResult = {
  purpose: string;
  start_date: string;
  end_date: string;
  lucky_dates: LuckyDateItem[];
};

function formatDate(iso: string) {
  const d = new Date(iso);
  const days = ["일", "월", "화", "수", "목", "금", "토"];
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${days[d.getDay()]})`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso: string, n: number) {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function LuckyDate() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  const [purpose, setPurpose] = useState("");
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(addDays(today(), 29));
  const [result, setResult] = useState<LuckyDateResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) {
    navigate("/login");
    return null;
  }

  async function handleSubmit() {
    if (!purpose) {
      setError("목적을 선택해 주세요.");
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await client.post("/features/lucky-date", {
        purpose,
        start_date: startDate,
        end_date: endDate,
      });
      setResult(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "길일을 찾을 수 없습니다.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">📅 택일(擇日)</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">사주 오행으로 찾는 나의 길일</p>
          </div>
        </div>

        {/* 입력 폼 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5 mb-4">
          {/* 목적 선택 */}
          <p className="text-sm font-bold text-[var(--color-text-primary)] mb-3">어떤 일을 위한 날짜인가요?</p>
          <div className="grid grid-cols-3 gap-2 mb-5">
            {PURPOSES.map((p) => (
              <button
                key={p}
                onClick={() => setPurpose(p)}
                className={`
                  flex flex-col items-center gap-1 py-2.5 rounded-xl border text-xs font-medium transition-colors
                  ${p === "기타" ? "col-span-3" : ""}
                  ${purpose === p
                    ? "bg-[var(--color-accent)] border-[var(--color-accent)] text-[var(--color-primary)]"
                    : "bg-[var(--color-bg)] border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-accent)]"
                  }
                `}
              >
                <span className="text-lg">{PURPOSE_ICON[p]}</span>
                <span>{p}</span>
              </button>
            ))}
          </div>

          {/* 기간 선택 */}
          <p className="text-sm font-bold text-[var(--color-text-primary)] mb-3">기간을 선택하세요 (최대 90일)</p>
          <div className="flex gap-3 items-center">
            <div className="flex-1">
              <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">시작일</label>
              <input
                type="date"
                value={startDate}
                min={today()}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-[var(--color-border-default)] rounded-xl px-3 py-2 text-sm text-[var(--color-text-primary)] bg-[var(--color-bg)] focus:outline-none focus:border-[var(--color-border-accent)]"
              />
            </div>
            <span className="text-[var(--color-border-default)] mt-4">~</span>
            <div className="flex-1">
              <label className="text-xs text-[var(--color-text-tertiary)] block mb-1">종료일</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-[var(--color-border-default)] rounded-xl px-3 py-2 text-sm text-[var(--color-text-primary)] bg-[var(--color-bg)] focus:outline-none focus:border-[var(--color-border-accent)]"
              />
            </div>
          </div>

          {error && (
            <p className="mt-3 text-xs text-[var(--color-error)] text-center">{error}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading || !purpose}
            className="mt-5 w-full py-3 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity"
          >
            {loading ? "길일 찾는 중..." : "✨ 길일 찾기"}
          </button>
        </div>

        {/* 로딩 */}
        {loading && (
          <div className="flex flex-col items-center py-16 gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-[var(--color-text-tertiary)]">럭키가 좋은 날을 찾아오는 중이에요</p>
          </div>
        )}

        {/* 결과 */}
        {result && !loading && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">{PURPOSE_ICON[result.purpose]}</span>
              <h2 className="text-base font-bold text-[var(--color-text-primary)]">
                {result.purpose}에 좋은 날 {result.lucky_dates.length}개
              </h2>
            </div>
            <div className="space-y-3">
              {result.lucky_dates.map((item, idx) => (
                <div
                  key={item.date}
                  className={`
                    border rounded-2xl p-4
                    ${idx === 0
                      ? "border-[var(--color-accent)]/40 bg-[var(--color-accent)]/10"
                      : "bg-[var(--color-surface)] border-[var(--color-border-default)]"
                    }
                  `}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      {idx === 0 && (
                        <span className="text-[10px] bg-[var(--color-accent)] text-[var(--color-primary)] px-2 py-0.5 rounded font-bold mr-2">
                          최고길일
                        </span>
                      )}
                      <p className={`text-sm font-bold ${idx === 0 ? "text-[var(--color-accent)]" : "text-[var(--color-text-primary)]"} mt-1`}>
                        {formatDate(item.date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[var(--color-text-tertiary)]">일진</p>
                      <p className="text-base font-bold text-[var(--color-text-primary)]">{item.day_pillar}일</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 mb-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ELEMENT_COLOR[item.gan_element] ?? "bg-[var(--color-surface)] text-[var(--color-text-tertiary)]"}`}>
                      천간 {item.gan_element}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ELEMENT_COLOR[item.ji_element] ?? "bg-[var(--color-surface)] text-[var(--color-text-tertiary)]"}`}>
                      지지 {item.ji_element}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">{item.reason}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() =>
                navigate("/counseling", {
                  state: { autoStart: true, category: "general", mode: "chat" },
                })
              }
              className="mt-4 w-full py-3 bg-[var(--color-surface)] border border-[var(--color-accent)]/30 text-[var(--color-accent)] rounded-xl text-sm font-bold hover:bg-[var(--color-accent)]/10 transition-colors"
            >
              💬 더 자세히 상담하기
            </button>
            <p className="text-center text-[var(--color-text-disabled)] text-[11px] mt-3">
              * 절기 근사값 기반 계산입니다. 정밀한 만세력과 다를 수 있습니다.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
